// src/App.tsx
// ============================================================================
// FILE ROLE: Root application component - routing and auth event handling
//
// TRUST BOUNDARY: public/unauth routing shell + authed session events
//
// INPUTS:
//   - URL hash route (e.g., /#/terms, /#/admin)
//   - Amplify auth Hub events (tokenRefresh_failure, signedIn, signedOut)
//
// OUTPUTS:
//   - Rendered route components
//   - Toast notifications for user feedback
//   - Auth state changes (signOut on token failure)
//
// SECURITY NOTES:
//   - Admin routes enforce server-side authorization via Cognito groups
//   - Frontend checks prevent UI access but backend validation is required
//   - Token refresh failures trigger immediate signOut
// ============================================================================

import { HashRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Hub } from "aws-amplify/utils";
import { signOut, fetchAuthSession } from "aws-amplify/auth";
import { Toaster } from "react-hot-toast";

import PresaleLandingP from "./components/presaleLandingP";
import TermsAndConditions from "./components/TermsAndConditions";
import PrivacyPolicy from "./components/PrivacyPolicy";
import CookiesPolicy from "./components/CookiesPolicy";
import ContactUs from "./components/ContactUs";
import AdminPortal from "./components/AdminPortal";
import NotFound from "./components/NotFound";
import FeedbackButton from './components/FeedbackButton';
import HeartsRiseOverlay from './components/HeartsRiseOverlay';


// ============================================================================
// PROTECTED ADMIN ROUTE COMPONENT
// ============================================================================
// Checks if user has admin permissions before rendering AdminPortal
// Non-admins are redirected to home page
// ============================================================================
function ProtectedAdminRoute() {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  useEffect(() => {
    checkAdminAccess();
  }, []);

const checkAdminAccess = async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const tokens = session.tokens;

      // No tokens — let through so AdminPortal's Authenticator can show sign-in form.
      if (!tokens) {
        setIsAdmin(true);
        return;
      }

      const groups = tokens.accessToken?.payload['cognito:groups'] as string[] | undefined;
      setIsAdmin(groups?.includes('Admins') ?? false);
    } catch {
      // Session lookup failed — fail closed.
      setIsAdmin(false);
    }
  };

  // Loading state
  if (isAdmin === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-branddark mx-auto mb-4"></div>
          <p className="text-gray-600">Checking permissions...</p>
        </div>
      </div>
    );
  }

  // Redirect non-admins
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  // Render admin portal for admins
  return <AdminPortal />;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // ============================================================================
  // AUTH HUB LISTENER
  // ============================================================================
  // Handles Amplify auth events for token refresh failures
  // ============================================================================
  useEffect(() => {
    // Initialise auth state from current session on mount
    fetchAuthSession().then(session => {
      setIsAuthenticated(!!session.tokens);
    }).catch(() => setIsAuthenticated(false));

    const hubListenerCancel = Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "tokenRefresh_failure":
          void ["🔒 Token refresh failed - signing out"];
          setIsAuthenticated(false);
          signOut().catch((err) => {
            void ["❌ SignOut failed after token refresh failure:", err];
          });
          break;

        case "signedIn":
          void ["✅ User signed in"];
          setIsAuthenticated(true);
          break;

        case "signedOut":
          void ["👋 User signed out"];
          setIsAuthenticated(false);
          break;

        case "tokenRefresh":
          void ["🔄 Token refreshed successfully"];
          break;
      }
    });

    return () => hubListenerCancel();
  }, []);

  return (
    <>
<Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: "#fff",
            color: "#1f2937",
            borderRadius: "12px",
            border: "2px solid #f46d6d",
            fontWeight: "500",
            boxShadow: "0 4px 20px rgba(244, 109, 109, 0.2)",
          },
          success: {
            iconTheme: {
              primary: "#f46d6d",
              secondary: "#fff",
            },
          },
          error: {
            iconTheme: {
              primary: "#e54c4c",
              secondary: "#fff",
            },
            style: {
              border: "2px solid #e54c4c",
              boxShadow: "0 4px 20px rgba(229, 76, 76, 0.2)",
            },
          },
        }}
      />
      {isAuthenticated && <FeedbackButton />}
      <Router>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<PresaleLandingP />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookies" element={<CookiesPolicy />} />
          <Route path="/contact" element={<ContactUs />} />

          {/* Protected admin route - checks Cognito groups */}
          <Route path="/admin/*" element={<ProtectedAdminRoute />} />

          {/* 404 fallback */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
      <HeartsRiseOverlay />
    </>
  );
}

export default App;